import { Typography } from '@euler/app/components/typography';
import { CardNotice } from '@euler/app/flows/ai-suite/components/ChatCard';
import { PrimaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiAnswerAction } from '@euler/model/bi/answer';
import { BiUploadView } from '@euler/model/bi/upload';
import { memo, useState } from 'react';
import { View } from 'react-native';
import { useBiTap } from '../../bi-visualization/functions/useBiTap';
import { SheetRow } from './SheetRow';

/**
 * Which sheets to import: ticked ones each become their own dataset, sent
 * in one tap. The sheets are listed in the file's order.
 */
export const SheetPicker = memo(({ view }: { view: BiUploadView }) => {
  const { rpx } = useResponsiveContext();
  const { pending, busy, tap } = useBiTap();
  const [selected, setSelected] = useState<string[]>([]);
  // A refused tap leaves the sheets to choose again.
  const disabled = busy || !!pending;
  const toggle = (name: string) =>
    setSelected(current =>
      current.includes(name)
        ? current.filter(entry => entry !== name)
        : [...current, name],
    );
  const submit = () => {
    // The file's order, whatever order they were ticked in.
    const sheets = view.sheets
      .map(sheet => sheet.name)
      .filter(name => selected.includes(name));
    tap({ action: BiAnswerAction.SelectSheets, uploadId: view.id, sheets });
  };
  const open = view.sheets.some(sheet => !sheet.block);
  return (
    <View style={{ gap: rpx(10) }}>
      <Typography.Text
        size={rpx(13)}
        lineHeight={rpx(20)}
        color={aiColors.foreground}
      >
        这个文件有 {view.sheets.length}{' '}
        个工作表。勾选要导入的，每个工作表会各自成为一份数据。
      </Typography.Text>
      <View style={{ gap: rpx(8) }}>
        {view.sheets.map(sheet => (
          <SheetRow
            key={sheet.name}
            sheet={sheet}
            rowLimit={view.rowLimit}
            selected={selected.includes(sheet.name)}
            disabled={disabled}
            onToggle={() => toggle(sheet.name)}
          />
        ))}
      </View>
      {open ? (
        <PrimaryButton
          title={
            selected.length
              ? `导入选中的 ${selected.length} 个工作表`
              : '请勾选要导入的工作表'
          }
          loading={!!pending}
          disabled={disabled || !selected.length}
          onPress={submit}
        />
      ) : (
        <CardNotice
          tone="danger"
          text="没有可以导入的工作表，请调整文件后重新上传。"
        />
      )}
    </View>
  );
});
